import { isApple } from './shortcuts'

export interface ParsedBinding {
	mod: boolean
	shift: boolean
	alt: boolean
	keyToken: string
}

export function parseBinding(keys: string): ParsedBinding {
	let mod = false
	let shift = false
	let alt = false
	let keyToken = ''
	for (const token of keys.split('-')) {
		const lower = token.toLowerCase()
		if (lower === 'mod') {
			mod = true
		} else if (lower === 'shift') {
			shift = true
		} else if (lower === 'alt') {
			alt = true
		} else {
			keyToken = token
		}
	}
	return { mod, shift, alt, keyToken }
}

/** A single letter matches its physical key (KeyZ); a named key such as Space already is one. */
function codeForToken(token: string): string {
	return token.length === 1 ? `Key${token.toUpperCase()}` : token
}

/** A bare punctuation character, e.g. `?` or `[` — never a letter, a named key, or a digit. */
function isPunctuationToken(token: string): boolean {
	return token.length === 1 && !/[a-zA-Z0-9]/.test(token)
}

interface MatchBindingOptions {
	matchByCode?: boolean
	/**
	 * A handful of bindings predate this registry and shipped checking "Cmd or Ctrl, on any platform"
	 * rather than the platform-specific key — true keeps that exact behavior instead of tightening it.
	 */
	looseModifier?: boolean
}

/**
 * Matches a `keys` binding (e.g. `'Mod-K'`, `'Alt-Z'`, `'X'`) against a keydown event. Mod resolves
 * to Cmd on Apple hardware and Ctrl elsewhere, and modifiers not named in the binding must be unheld,
 * so a binding never fires alongside a modifier combination it didn't ask for.
 */
const FUNCTION_KEY = /^F\d{1,2}$/

export function matchesBinding(
	event: KeyboardEvent,
	keys: string,
	{ matchByCode = false, looseModifier = false }: MatchBindingOptions = {},
): boolean {
	// A dead key (composing an accent) or a function key never completes a letter or punctuation
	// binding, even when `matchByCode` would otherwise line its physical position up with one.
	if (event.key === 'Dead' || FUNCTION_KEY.test(event.key)) {
		return false
	}

	const { mod, shift, alt, keyToken } = parseBinding(keys)

	if (mod) {
		if (looseModifier) {
			if (!(event.metaKey || event.ctrlKey)) {
				return false
			}
		} else {
			const primaryMod = isApple ? event.metaKey : event.ctrlKey
			const secondaryMod = isApple ? event.ctrlKey : event.metaKey
			if (!primaryMod || secondaryMod) {
				return false
			}
		}
	} else if (event.metaKey || event.ctrlKey) {
		return false
	}

	// Shift does not count for punctuation: `?` is Shift+/ on a US layout but its own key elsewhere,
	// and `event.key` already names the right character either way.
	const shiftMatters = !isPunctuationToken(keyToken)
	if ((shiftMatters && shift !== event.shiftKey) || alt !== event.altKey) {
		return false
	}

	return matchByCode ? event.code === codeForToken(keyToken) : event.key.toLowerCase() === keyToken.toLowerCase()
}
